'use client';

import { useToolUsageReport } from '@/components/tools/use-tool-usage-report';
import { useEffect, useRef, useState } from 'react';

import {
  addNoise,
  adjustContrast,
  adjustSaturation,
  sharpen,
} from '@/lib/browser-image-effects-tools';

const MAX_WORK = 1000;

// Load a data URL into an Image and resolve when ready.
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('load failed'));
    img.src = src;
  });
}

export default function BrowserDeepFryImage() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [intensity, setIntensity] = useState(0.7);
  const [seed, setSeed] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  // Fires once the fried image exists, i.e. the user actually ran it.
  useToolUsageReport('deep-fry-image', Boolean(downloadUrl));
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sourceUrlRef = useRef<string | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const runRef = useRef(0);

  useEffect(() => {
    return () => {
      if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
      if (downloadUrlRef.current) URL.revokeObjectURL(downloadUrlRef.current);
    };
  }, []);

  function handleFile(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    setError(null);
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    const url = URL.createObjectURL(file);
    sourceUrlRef.current = url;
    const img = new Image();
    img.onload = () => setImage(img);
    img.onerror = () => setError('That image could not be loaded.');
    img.src = url;
  }

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!image || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const runId = runRef.current + 1;
    runRef.current = runId;

    async function fry() {
      const ratio = Math.min(1, MAX_WORK / Math.max(image!.naturalWidth, image!.naturalHeight));
      const w = Math.max(1, Math.round(image!.naturalWidth * ratio));
      const h = Math.max(1, Math.round(image!.naturalHeight * ratio));
      canvas.width = w;
      canvas.height = h;

      // Slight warp: draw the source zoomed in a touch so edges crunch.
      const zoom = 1 + intensity * 0.05;
      const dw = w * zoom;
      const dh = h * zoom;
      ctx!.drawImage(image!, (w - dw) / 2, (h - dh) / 2, dw, dh);

      // Crank saturation, contrast, sharpen, then add noise.
      const imageData = ctx!.getImageData(0, 0, w, h);
      adjustSaturation(imageData.data, 1 + intensity * 2.6);
      adjustContrast(imageData.data, 1 + intensity * 1.3);
      const sharpened = sharpen(imageData.data, w, h, intensity * 1.6);
      imageData.data.set(sharpened);
      addNoise(imageData.data, intensity * 0.5, seed * 2654435761);
      ctx!.putImageData(imageData, 0, 0);

      // JPEG-artifact recompression: repeated low-quality round trips.
      const quality = 0.28 - intensity * 0.18;
      const passes = 1 + Math.round(intensity * 3);
      for (let pass = 0; pass < passes; pass += 1) {
        const dataUrl = canvas.toDataURL('image/jpeg', Math.max(0.05, quality));
        const reloaded = await loadImage(dataUrl);
        if (runRef.current !== runId) return; // superseded by a newer run
        ctx!.drawImage(reloaded, 0, 0, w, h);
      }

      canvas.toBlob(
        (blob) => {
          if (!blob || runRef.current !== runId) return;
          if (downloadUrlRef.current) URL.revokeObjectURL(downloadUrlRef.current);
          const url = URL.createObjectURL(blob);
          downloadUrlRef.current = url;
          setDownloadUrl(url);
        },
        'image/jpeg',
        0.6,
      );
    }

    void fry();
  }, [image, intensity, seed]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Deep fry</span>
        <h2 className="tool-live-title">Deep-fry an image (meme effect)</h2>
        <p className="tool-live-subtitle">
          Upload an image and blast it with saturation, contrast, sharpening, noise, and crunchy
          JPEG artifacts. Drag the slider or re-randomize, then download. Runs entirely in your
          browser. Nothing is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}

          {image ? (
            <>
              <label className="tool-live-field tool-live-field-full">
                <span>Fry level: {Math.round(intensity * 100)}%</span>
                <input
                  max={1}
                  min={0.1}
                  onChange={(event) => setIntensity(Number(event.target.value))}
                  step={0.02}
                  type="range"
                  value={intensity}
                />
              </label>

              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => setSeed((s) => s + 1)}
                  type="button"
                >
                  Re-randomize
                </button>
                {downloadUrl ? (
                  <a
                    className="button button-secondary"
                    download="deep-fried.jpg"
                    href={downloadUrl}
                  >
                    Download JPG
                  </a>
                ) : null}
              </div>
            </>
          ) : (
            <p className="tool-live-panel-note">Pick an image to deep-fry it.</p>
          )}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            Processing runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
